import { useMemo, useState } from "react";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/ui/Toast";
import {
  useAddMyUserGoalMutation,
  useDeleteMyUserGoalMutation,
} from "../../api/userApi";
import { todayString } from "../groups/components/memberEntry/memberDayHelpers";
import Drawer from "../../components/ui/Drawer";
import { SoftButton, cls } from "../hatim/soft/SoftKit";
import GoalWizardDrawer from "./GoalWizard";
import GoalListRow from "./GoalListRow";
import ConfirmDeleteGoal from "./ConfirmDeleteGoal";
import { toGoalRequest } from "./goalRules";

/**
 * Hedeflerim çekmecesi (Terakkim'deki Hedeflerim kutusunun kalemiyle açılır;
 * mobil Hedef Detayları gibi). Hedef düzenlenmez: özel hedef (kendi eklediği)
 * silinip yeniden oluşturulur; genel hedefler (grup yöneticisinden) kilitli.
 * Ekleme üstte açılan sihirbaz çekmecesinde.
 */
export default function MyGoalsDrawer({ open, onClose }) {
  const { t } = useScriptMode();
  const { user, setUser } = useAuth();
  const toast = useToast();

  const [addGoal, { isLoading: adding }] = useAddMyUserGoalMutation();
  const [deleteGoal, { isLoading: deleting }] = useDeleteMyUserGoalMutation();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const goals = useMemo(() => (Array.isArray(user?.goalDetail) ? user.goalDetail : []), [user]);
  const customCount = goals.filter((g) => g.isCustom !== false).length;
  const busy = adding || deleting;

  // Sunucu güncel listenin tamamını döndürüyor; Terakkim de buradan okuyor.
  const applyGoals = (res) => {
    const goalDetail = res?.data?.goalDetail;
    if (Array.isArray(goalDetail)) setUser({ ...(user || {}), goalDetail });
  };

  const handleSubmit = async (value) => {
    const body = { ...toGoalRequest(value), localDate: todayString() };
    try {
      const res = await addGoal(body).unwrap();
      applyGoals(res);
      toast.success(t("goal_modal_added"));
      setWizardOpen(false);
    } catch (err) {
      toast.error(err?.message || t("goal_modal_error"));
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await deleteGoal({
        goalLabel: toDelete.label,
        activity: toDelete.activity || "okuma",
        localDate: todayString(),
      }).unwrap();
      applyGoals(res);
      toast.success(t("goal_modal_deleted"));
      setToDelete(null);
    } catch (err) {
      toast.error(err?.message || t("goal_modal_error"));
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={t("nav_my_goals")}
        subtitle={`${t("my_goals_subtitle")} · ${t("my_goals_custom_count", { count: customCount })}`}
        titleIcon={<FlagOutlinedIcon sx={{ fontSize: 20 }} />}
        footer={
          <div className="flex gap-2">
            <SoftButton tone="soft" fullWidth onClick={onClose}>
              {t("manage_goals_close")}
            </SoftButton>
            <SoftButton tone="primary" fullWidth onClick={() => setWizardOpen(true)} disabled={busy}>
              {t("my_goals_add")}
            </SoftButton>
          </div>
        }
      >
        {goals.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-12 px-6 text-center">
            <p className={`text-md ${cls.title}`}>{t("my_goals_empty_title")}</p>
            <p className={`text-base ${cls.muted}`}>{t("my_goals_empty_body")}</p>
          </div>
        ) : (
          <ul>
            {goals.map((goal) => {
              const custom = goal.isCustom !== false;
              return (
                <GoalListRow
                  key={`${goal.label}::${goal.activity || "okuma"}`}
                  goal={goal}
                  busy={busy}
                  badge={{
                    tone: custom ? "custom" : "system",
                    label: t(custom ? "my_goals_badge_custom" : "my_goals_badge_general"),
                  }}
                  onDelete={custom ? () => setToDelete(goal) : undefined}
                />
              );
            })}
          </ul>
        )}
        <p className={`px-4 py-3 text-sm ${cls.faint}`}>{t("my_goals_info")}</p>
      </Drawer>

      <GoalWizardDrawer
        open={wizardOpen}
        mode="add"
        submitting={adding}
        onSubmit={handleSubmit}
        onClose={() => setWizardOpen(false)}
      />
      <ConfirmDeleteGoal
        open={!!toDelete}
        label={toDelete?.label}
        loading={deleting}
        onCancel={() => setToDelete(null)}
        onConfirm={handleDelete}
      />
    </>
  );
}
